import { IAIProvider } from '../interfaces/IAIProvider';
import { RephraseOptions, RephraseResult } from '../../types/aiTypes';
import { log, logError } from '../../../utils/logging';
import { AppError } from '../../../utils/errorHandling';
import apiService from '../../../services/api';

export class VertexAIProvider implements IAIProvider {
  private paragraphXml?: string;  // Store XML for backend processing

  /**
   * Set the paragraph XML to be sent to backend
   */
  setParagraphXml(xml: string | undefined): void {
    this.paragraphXml = xml;
  }

  async rephrase(text: string, _options?: RephraseOptions, completeRephrase: boolean = false): Promise<RephraseResult> {
    if (!text || text.trim().length === 0) {
      throw new AppError('Text cannot be empty', 'EMPTY_TEXT', 'Please select some text to rephrase.');
    }

    try {
      log(`Sending ${completeRephrase ? 'complete rephrase' : 'polish'} request to backend`, {
        hasXml: !!this.paragraphXml,
        xmlLength: this.paragraphXml?.length || 0,
        xmlPreview: this.paragraphXml?.substring(0, 200) || 'N/A'
      });

      // Use the API service to call the backend endpoint
      // For complete rephrase, use rephrase endpoint; for polish, use polish endpoint
      // Pass XML if available (language is auto-detected by the API)
      const apiResponse = completeRephrase
        ? await apiService.rephraseText(text, undefined, this.paragraphXml)
        : await apiService.polishText(text, true, this.paragraphXml);
      
      if (!apiResponse.success || !apiResponse.data) {
        throw new AppError(
          'Invalid response from AI service',
          'INVALID_RESPONSE',
          'The AI service returned an invalid response. Please try again.'
        );
      }

      log(`Received response from backend`, {
        hasParagraphXml: !!apiResponse.data.paragraph_xml,
        paragraphXmlLength: apiResponse.data.paragraph_xml?.length || 0
      });

      return {
        rephrasedText: apiResponse.data.processed_text,
        confidence: 0.9, // Default confidence
        paragraphXml: apiResponse.data.paragraph_xml,  // Return XML if provided by backend
      };
    } catch (error) {
      logError('Rephrase request failed', error);
      
      // Re-throw AppError as-is
      if (error instanceof AppError) {
        throw error;
      }
      
      // Wrap other errors
      throw new AppError(
        'Failed to rephrase text',
        'REPHRASE_FAILED',
        error instanceof Error ? error.message : 'Unable to rephrase the selected text. Please try again.'
      );
    } finally {
      // Clear XML after request
      this.paragraphXml = undefined;
    }
  }
}

